/**
 * Centralized API Client Composable
 * Single source of truth for all API communication
 * Uses contract-first API client from @monorepo/api-client
 */

import { ref, computed } from 'vue';
import { createFrontendClient, type FrontendClient } from '@monorepo/api-client/frontend';
import { config } from '@/config';

// ============================================================================
// Singleton API Client
// ============================================================================

let _apiClient: FrontendClient | null = null;

function createApiClient(): FrontendClient {
	if (_apiClient) {
		return _apiClient;
	}

	try {
		_apiClient = createFrontendClient({
			baseURL: config.apiUrl,
			timeout: 30000,
		});

		// console.log('✅ [API] API client initialized successfully');

		// ✅ CRITICAL: Register session refresh callback IMMEDIATELY after creation
		// This ensures ALL API calls (tracking, analytics, etc.) can auto-recover from CSRF errors
		setupSessionRefreshCallback(_apiClient);

		return _apiClient;
	} catch (error) {
		console.error('❌ [API] Failed to initialize API client:', error);
		throw error;
	}
}

/**
 * Setup automatic CSRF token refresh for the API client
 * This is called automatically when the client is created
 */
function setupSessionRefreshCallback(client: FrontendClient): void {
	if (!client.$http || typeof client.$http.setSessionRefreshCallback !== 'function') {
		console.warn('⚠️ [API] HTTP client does not support session refresh callback');
		return;
	}

	const refreshSessionToken = async (): Promise<void> => {
		// console.log('🔄 [API] Refreshing CSRF token due to 403 error');

		// Log current token before refresh
		// const oldSessionData = localStorage.getItem('ulrich-session');
		// console.log('📋 [API] Current localStorage before refresh:', oldSessionData);

		try {
			// Use the same client to verify session
			const response = await client.session.verify();
			// console.log('✅ [API] CSRF token refreshed successfully');
			// console.log('📋 [API] New session data from backend:', {
			// 	sessionId: response.data.sessionId?.substring(0, 12) + '...',
			// 	csrfToken: response.data.csrfToken?.substring(0, 15) + '...',
			// 	userId: response.data.userId
			// });

			// Update localStorage directly
			// Note: This will trigger the watch() in session store, updating refs automatically
			const newSessionData = {
				sessionId: response.data.sessionId,
				csrfToken: response.data.csrfToken,
				userId: response.data.userId
			};
			localStorage.setItem('ulrich-session', JSON.stringify(newSessionData));
			// console.log('💾 [API] localStorage updated successfully');

			// Verify localStorage was actually updated
			// const verifyData = localStorage.getItem('ulrich-session');
			// const parsed = JSON.parse(verifyData!);
			// console.log('✓ [API] Verification - localStorage now contains:', {
			// 	sessionId: parsed.sessionId?.substring(0, 12) + '...',
			// 	csrfToken: parsed.csrfToken?.substring(0, 15) + '...',
			// 	matches: parsed.csrfToken === response.data.csrfToken
			// });

			// ✅ CRITICAL: Dispatch custom event to notify session store in same tab
			// storage event only fires across tabs, not in the same tab
			window.dispatchEvent(new CustomEvent('session-refreshed', {
				detail: {
					sessionId: response.data.sessionId,
					csrfToken: response.data.csrfToken,
					userId: response.data.userId
				}
			}));
		} catch (error) {
			console.error('❌ [API] Failed to refresh CSRF token', error);
			// Clear invalid session
			localStorage.removeItem('ulrich-session');
			window.dispatchEvent(new CustomEvent('session-cleared'));
			throw error;
		}
	};

	client.$http.setSessionRefreshCallback(refreshSessionToken);
	// console.log('✅ [API] Registered CSRF token auto-refresh callback');
}

// ============================================================================
// Composable
// ============================================================================

export function useApiClient() {
	const isInitialized = ref(false);
	const error = ref<string | null>(null);

	// Lazy initialization
	const client = computed(() => {
		try {
			if (!_apiClient) {
				_apiClient = createApiClient();
				isInitialized.value = true;
				error.value = null;
			}
			return _apiClient;
		} catch (err) {
			error.value = err instanceof Error ? err.message : 'Unknown API client error';
			throw err;
		}
	});

	return {
		// Contract-first API client with all endpoints
		client,

		// State
		isInitialized: computed(() => isInitialized.value),
		error: computed(() => error.value),
	};
}

// ============================================================================
// Convenience Exports
// ============================================================================

/**
 * Get the singleton API client instance
 * @throws Error if not properly configured
 */
export function getApiClient(): FrontendClient {
	return createApiClient();
}
